import { Button, Card, Input, Popconfirm, Space, Table, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSectionSelect, EducationSubjectSelect } from '@/pages/education/components/EducationLookupSelects';
import { LMS_HOME_PATH, lmsErrorMessage, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Resource = {
  id: string; title: string; resource_url: string; notes?: string | null;
  section_name?: string; subject_code?: string; subject_name?: string;
};

export default function ResourceListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { canManage } = useLmsAccess();
  const q = params.get('q') || '';
  const sectionId = params.get('section_id') || undefined;
  const subjectId = params.get('subject_id') || undefined;
  const list = useQuery({
    queryKey: ['education-lms-resources', q, sectionId, subjectId],
    queryFn: async () => (await api.get<Resource[]>('/education/lms/resources', {
      params: { q: q || undefined, section_id: sectionId, subject_id: subjectId },
    })).data || [],
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/education/lms/resources/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['education-lms-resources'] });
      queryClient.invalidateQueries({ queryKey: ['education-lms-summary'] });
    },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to delete resource')),
  });
  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title={t('lmsResources', { defaultValue: 'Resources' })}
      description={t('lmsResourcesIntro', { defaultValue: 'Shared links to learning material.' })}
      extra={<Space>
        <Button onClick={() => navigate(LMS_HOME_PATH)}>{t('lmsBackToHub', { defaultValue: 'Back to classroom' })}</Button>
        {canManage ? (
          <Button type="primary" onClick={() => navigate('/education/lms/resources/new')}>
            {t('lmsNewResource', { defaultValue: 'New resource' })}
          </Button>
        ) : null}
      </Space>}
    >
      <Card size="small">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear defaultValue={q} style={{ width: 260 }}
            placeholder={t('search', { defaultValue: 'Search' })}
            onSearch={(value) => setFilter('q', value.trim())}
          />
          <EducationSectionSelect allowClear style={{ width: 240 }} value={sectionId} onChange={(v) => setFilter('section_id', v)} />
          <EducationSubjectSelect allowClear style={{ width: 240 }} value={subjectId} onChange={(v) => setFilter('subject_id', v)} />
        </Space>
        <Table
          rowKey="id" size="small" loading={list.isLoading} dataSource={list.data}
          pagination={{ pageSize: 25 }}
          locale={{ emptyText: t('lmsNoRows', { defaultValue: 'Nothing here yet' }) }}
          columns={[
            { title: t('title', { defaultValue: 'Title' }), dataIndex: 'title' },
            {
              title: t('lmsResourceUrl', { defaultValue: 'Link' }),
              render: (_: unknown, r: Resource) => <a href={r.resource_url} target="_blank" rel="noreferrer">{t('open', { defaultValue: 'Open' })}</a>,
              width: 90,
            },
            { title: t('section', { defaultValue: 'Section' }), dataIndex: 'section_name', render: (v?: string) => v || '—' },
            { title: t('subject', { defaultValue: 'Subject' }), render: (_: unknown, r: Resource) => r.subject_name || r.subject_code || '—' },
            { title: t('lmsNotes', { defaultValue: 'Notes' }), dataIndex: 'notes', ellipsis: true },
            {
              title: t('actions', { defaultValue: 'Actions' }), width: 170,
              render: (_: unknown, r: Resource) => canManage ? (
                <Space>
                  <Button size="small" onClick={() => navigate(`/education/lms/resources/${r.id}/edit`)}>{t('edit', { defaultValue: 'Edit' })}</Button>
                  <Popconfirm title={t('lmsDelete', { defaultValue: 'Delete' })} onConfirm={() => remove.mutate(r.id)}>
                    <Button size="small" danger>{t('lmsDelete', { defaultValue: 'Delete' })}</Button>
                  </Popconfirm>
                </Space>
              ) : null,
            },
          ]}
        />
      </Card>
    </EducationPageChrome>
  );
}
